import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { useDesignSubmissions } from '../../../contexts/DesignSubmissionsContext';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
`;

const sway = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const wave = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0a1628;
  /* Leave room for fixed nav + stacked announcement/event banners */
  padding-top: calc(198px + env(safe-area-inset-top, 0px));

  @media (max-width: 768px) {
    padding-top: calc(172px + env(safe-area-inset-top, 0px));
  }
`;

const Hero = styled.div`
  background: linear-gradient(180deg, #0a1628 0%, #12284a 50%, #1a3560 100%);
  padding: 4rem 1.5rem 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200%;
    height: 80px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 80'%3E%3Cpath d='M0,40 C150,80 350,0 600,40 C850,80 1050,0 1200,40 L1200,80 L0,80 Z' fill='%23f8f3ea'/%3E%3C/svg%3E") repeat-x bottom;
    background-size: 1200px 80px;
    animation: ${wave} 8s linear infinite;
  }
`;

const Stars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;

  &::before, &::after {
    content: '✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧';
    position: absolute;
    color: rgba(255, 255, 255, 0.08);
    font-size: 1.2rem;
    letter-spacing: 3rem;
    white-space: nowrap;
  }

  &::before { top: 1.5rem; }
  &::after { top: 4rem; left: 4rem; }
`;

const FlagBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  padding: 0.5rem 1.25rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const HeroEmoji = styled.div`
  font-size: 5rem;
  margin-bottom: 1rem;
  animation: ${float} 3.5s ease-in-out infinite;
  display: inline-block;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4));
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(2.25rem, 6vw, 4rem);
  font-family: 'Cormorant Garamond', serif;
  font-weight: 700;
  margin: 0 0 0.5rem;
  line-height: 1.05;
  text-shadow: 0 2px 20px rgba(0,0,0,0.3);
`;

const HeroAccent = styled.span`
  color: #f5c842;
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  max-width: 620px;
  margin: 0.75rem auto 1rem;
  line-height: 1.65;
`;

const PrizeBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #f5c842, #e8a820);
  color: #3d2800;
  border-radius: 14px;
  padding: 0.875rem 1.75rem;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(245, 200, 66, 0.35);
`;

const HeroLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const GalleryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 0.65rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.25s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: white;
    transform: translateY(-2px);
  }
`;

const ContentArea = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  animation: ${fadeIn} 0.5s ease both;
  background: #f8f3ea;
  position: relative;
`;

const HowItWorks = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const HowStep = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.25rem;
  text-align: center;
  border: 2px solid rgba(16, 29, 64, 0.06);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const HowEmoji = styled.div`
  font-size: 2.25rem;
  margin-bottom: 0.5rem;
  animation: ${sway} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  display: inline-block;
`;

const HowTitle = styled.p`
  font-weight: 700;
  color: #101d40;
  font-size: 0.9rem;
  margin: 0 0 0.25rem;
`;

const HowText = styled.p`
  color: #888;
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.4;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 4px 24px rgba(16, 29, 64, 0.08), 0 1px 4px rgba(16, 29, 64, 0.04);
  border: 2px solid rgba(16, 29, 64, 0.07);

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const FormTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #101d40;
  margin: 0 0 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormSubtitle = styled.p`
  color: #7a7a7a;
  margin: 0 0 2rem;
  font-size: 0.95rem;
  line-height: 1.55;

  strong {
    color: #101d40;
    font-weight: 700;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: #101d40;
  margin-bottom: 0.5rem;

  span.req {
    color: #9d6d49;
    margin-left: 2px;
  }

  span.hint {
    color: #aaa;
    font-weight: 400;
    margin-left: 0.5rem;
    font-size: 0.82rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e8e4dc;
  border-radius: 12px;
  font-size: 1rem;
  color: #101d40;
  background: #fef9f0;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #101d40;
    background: white;
    box-shadow: 0 0 0 3px rgba(16, 29, 64, 0.06);
  }

  &::placeholder { color: #bbb; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e8e4dc;
  border-radius: 12px;
  font-size: 1rem;
  color: #101d40;
  background: #fef9f0;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 110px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #101d40;
    background: white;
    box-shadow: 0 0 0 3px rgba(16, 29, 64, 0.06);
  }

  &::placeholder { color: #bbb; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const UploadZone = styled.div`
  border: 3px dashed ${props => props.hasFile ? '#9d6d49' : '#e8e4dc'};
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  background: ${props => props.hasFile ? 'rgba(157, 109, 73, 0.04)' : '#fef9f0'};
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;

  &:hover {
    border-color: #101d40;
    background: rgba(16, 29, 64, 0.02);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

const UploadIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 0.75rem;
  animation: ${sway} 2s ease-in-out infinite;
`;

const UploadText = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0 0 0.25rem;
  font-weight: 500;
`;

const UploadHint = styled.p`
  color: #bbb;
  font-size: 0.8rem;
  margin: 0;
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  max-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 2px solid #e8e4dc;

  img {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const FlagTips = styled.div`
  background: linear-gradient(135deg, rgba(10, 22, 40, 0.04), rgba(10, 22, 40, 0.06));
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const TipsTitle = styled.p`
  font-weight: 700;
  color: #101d40;
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const TipsList = styled.ul`
  margin: 0;
  padding: 0 0 0 1.25rem;
  color: #666;
  font-size: 0.875rem;
  line-height: 1.7;
`;

const ConsentBox = styled.div`
  background: rgba(245, 200, 66, 0.06);
  border: 2px solid rgba(245, 200, 66, 0.3);
  border-radius: 14px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 1px;
    accent-color: #101d40;
    cursor: pointer;
  }
`;

const ConsentText = styled.span`
  font-size: 0.875rem;
  color: #555;
  line-height: 1.55;

  strong { color: #101d40; }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1.1rem;
  background: linear-gradient(135deg, #0a1628, #12284a);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #12284a, #0a1628);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(10, 22, 40, 0.35);
  }

  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(16, 29, 64, 0.08);
  animation: ${fadeIn} 0.6s ease both;
  border: 2px solid rgba(245, 200, 66, 0.3);
`;

const SuccessEmoji = styled.div`
  font-size: 5rem;
  margin-bottom: 1.5rem;
  animation: ${float} 2.5s ease-in-out infinite;
  display: inline-block;
`;

const SuccessTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.5rem;
  color: #101d40;
  margin: 0 0 1rem;
`;

const SuccessText = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 0 0 0.75rem;
  line-height: 1.65;

  strong {
    color: #101d40;
    font-weight: 700;
  }
`;

const SuccessHighlight = styled.div`
  background: linear-gradient(135deg, #f5c842, #e8a820);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin: 1.25rem 0;
  color: #3d2800;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SuccessActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const ActionButton = styled(Link)`
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.25s ease;

  &.primary {
    background: #101d40;
    color: white;
    &:hover { background: #1a2f5e; transform: translateY(-2px); }
  }
`;

const AnotherButton = styled.button`
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  background: transparent;
  color: #101d40;
  border: 2px solid #101d40;
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover { background: #101d40; color: white; transform: translateY(-2px); }
`;

const ErrorText = styled.p`
  color: #c0392b;
  font-size: 0.85rem;
  margin: 0.35rem 0 0;
`;

const PrintTemplateBox = styled.div`
  background: linear-gradient(135deg, rgba(245, 200, 66, 0.14), rgba(157, 109, 73, 0.1));
  border: 2px solid rgba(157, 109, 73, 0.4);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
`;

const PrintTemplateCopy = styled.div`
  flex: 1;
  min-width: 220px;

  strong {
    display: block;
    color: #101d40;
    font-size: 1rem;
    margin-bottom: 0.35rem;
    font-family: 'Cormorant Garamond', serif;
    font-weight: 700;
  }

  span {
    font-size: 0.875rem;
    color: #555;
    line-height: 1.55;
  }
`;

const TemplatePdfLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.85rem 1.35rem;
  background: #101d40;
  color: white !important;
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 29, 64, 0.28);
    color: white !important;
  }
`;

/** Printable sail template PDF (see /public/pirate-sail-design-template.pdf) */
const SAIL_DESIGN_TEMPLATE_PDF = `${process.env.PUBLIC_URL || ''}/pirate-sail-design-template.pdf`;

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ChildrensArtSubmit = () => {
  const { addSubmission } = useDesignSubmissions();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    childName: '',
    age: '',
    email: '',
    school: '',
    title: '',
    description: '',
    imageData: null,
    imageFileName: '',
    consentPublic: false,
    consentTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submittedEntry, setSubmittedEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: null }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors(er => ({ ...er, image: 'Please select an image file (JPG, PNG, etc.)' }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setErrors(er => ({ ...er, image: 'Image must be smaller than 5MB.' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f => ({ ...f, imageData: ev.target.result, imageFileName: file.name }));
      setErrors(er => ({ ...er, image: null }));
    };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!form.childName.trim()) e.childName = 'Please enter a name';
    const ageNum = Number(form.age);
    if (!String(form.age).trim() || !Number.isInteger(ageNum) || ageNum < 1 || ageNum > 999)
      e.age = 'Please enter your age as a whole number';
    const emailTrim = form.email.trim();
    if (!emailTrim) e.email = 'Please enter an email address';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim))
      e.email = 'Please enter a valid email address';
    if (!form.title.trim()) e.title = 'Please give your sail design a title';
    if (!form.imageData) e.image = 'Please upload a photo of your sail design';
    if (!form.consentPublic) e.consentPublic = 'Please tick this box to continue';
    if (!form.consentTerms) e.consentTerms = 'Please tick this box to continue';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const entry = await addSubmission({
        childName: form.childName.trim(),
        age: parseInt(form.age, 10),
        email: form.email.trim(),
        school: form.school.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        imageData: form.imageData,
        imageFileName: form.imageFileName,
      });
      setSubmittedEntry({
        ...entry,
        childName: entry.childName ?? form.childName.trim(),
        title: entry.title ?? form.title.trim(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    setForm({
      childName: '',
      age: '',
      email: '',
      school: '',
      title: '',
      description: '',
      imageData: null,
      imageFileName: '',
      consentPublic: false,
      consentTerms: false,
    });
    setErrors({});
    setSubmitted(false);
    setSubmittedEntry(null);
  };

  return (
    <PageContainer>
      <Hero>
        <Stars />
        <FlagBanner>🏴‍☠️ Pirate Circus · Sail Design Competition</FlagBanner>
        <HeroEmoji>🏴‍☠️</HeroEmoji>
        <HeroTitle>
          Design Our <HeroAccent>Pirate Sail!</HeroAccent>
        </HeroTitle>
        <HeroSubtitle>
          Ahoy, young pirates! Design a sail for our mighty pirate ship at Belvoir Castle.
          The winning design will be sewn into a <strong style={{ color: '#f5c842' }}>real sail</strong> and hoisted on the ship in the adventure playground —
          and the winner also receives a <strong style={{ color: '#f5c842' }}>family ticket</strong> to{' '}
          Belvoir Castle <strong style={{ color: '#f5c842' }}>BOOM Circus</strong>.
        </HeroSubtitle>
        <PrizeBanner>
          🏆 Real pirate sail on the ship + family ticket to BOOM Circus!
        </PrizeBanner>
        <HeroLinks>
          <GalleryLink to="/childrens-art-gallery">
            🖼️ See All Entries
          </GalleryLink>
        </HeroLinks>
      </Hero>

      <ContentArea>
        {!submitted ? (
          <>
            <HowItWorks>
              <HowStep>
                <HowEmoji delay="0s">✏️</HowEmoji>
                <HowTitle>1. Design Your Sail</HowTitle>
                <HowText>Draw your best pirate sail — on paper, card, or digitally!</HowText>
              </HowStep>
              <HowStep>
                <HowEmoji delay="0.3s">📸</HowEmoji>
                <HowTitle>2. Take a Photo</HowTitle>
                <HowText>Take a clear photo of your finished design and upload it below.</HowText>
              </HowStep>
              <HowStep>
                <HowEmoji delay="0.6s">🏆</HowEmoji>
                <HowTitle>3. Win the Prizes</HowTitle>
                <HowText>
                  The winning sail is made for our pirate ship — plus the winner gets a family ticket to Belvoir Castle BOOM Circus!
                </HowText>
              </HowStep>
            </HowItWorks>

            <PrintTemplateBox>
              <PrintTemplateCopy>
                <strong>Print a sail template</strong>
                <span>
                  Download our PDF, print it at home, then colour or paint your design before you photograph it for your entry.
                </span>
              </PrintTemplateCopy>
              <TemplatePdfLink
                href={SAIL_DESIGN_TEMPLATE_PDF}
                target="_blank"
                rel="noopener noreferrer"
              >
                🖨️ Print template (PDF)
              </TemplatePdfLink>
            </PrintTemplateBox>

            <FormCard>
              <FormTitle>🏴‍☠️ Submit Your Sail Design</FormTitle>
              <FormSubtitle>
                Fill in the form below — a parent or guardian can help you.
                After you send it, your design stays <strong>in review</strong> until our team approves it — then it will appear on the public gallery for everyone to see.
              </FormSubtitle>

              <FlagTips>
                <TipsTitle>🗺️ Tips for a brilliant pirate sail:</TipsTitle>
                <TipsList>
                  <li>Think about bold shapes and strong colours — sails need to be seen from far away!</li>
                  <li>Classic pirate symbols: skull &amp; crossbones, swords, anchors, sea creatures, treasure chests…</li>
                  <li>Make it your own — add something unique that represents YOU, brave pirate!</li>
                  <li>Use crayons, felt tips, paint or even a computer — any medium is welcome.</li>
                </TipsList>
              </FlagTips>

              <form onSubmit={handleSubmit} noValidate>
                <TwoCol>
                  <FieldGroup>
                    <Label htmlFor="childName">Your Name <span className="req">*</span></Label>
                    <Input
                      id="childName"
                      name="childName"
                      value={form.childName}
                      onChange={handleChange}
                      placeholder="e.g. Captain Emily"
                      maxLength={60}
                    />
                    {errors.childName && <ErrorText>{errors.childName}</ErrorText>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="age">Your Age <span className="req">*</span></Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      value={form.age}
                      onChange={handleChange}
                      placeholder="e.g. 9"
                      min="1"
                    />
                    {errors.age && <ErrorText>{errors.age}</ErrorText>}
                  </FieldGroup>
                </TwoCol>

                <FieldGroup>
                  <Label htmlFor="email">
                    Parent or guardian email <span className="req">*</span>
                    <span className="hint">So we can contact you about the competition</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    maxLength={254}
                  />
                  {errors.email && <ErrorText>{errors.email}</ErrorText>}
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="school">
                    School
                    <span className="hint">Optional</span>
                  </Label>
                  <Input
                    id="school"
                    name="school"
                    value={form.school}
                    onChange={handleChange}
                    placeholder="e.g. Oakwood Primary School"
                    maxLength={200}
                    autoComplete="organization"
                  />
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="title">
                    Name Your Sail <span className="req">*</span>
                    <span className="hint">Give your sail a fearsome pirate name!</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder='e.g. "The Crimson Kraken" or "The Golden Skull"'
                    maxLength={80}
                  />
                  {errors.title && <ErrorText>{errors.title}</ErrorText>}
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="description">
                    Tell Us About Your Sail
                    <span className="hint">Optional</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="What does your sail design mean? What symbols did you use? What makes it special?"
                    maxLength={400}
                  />
                </FieldGroup>

                <FieldGroup>
                  <Label>Photo of Your Sail Design <span className="req">*</span></Label>
                  <UploadZone hasFile={!!form.imageData} onClick={() => fileInputRef.current?.click()}>
                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      onClick={e => e.stopPropagation()}
                    />
                    {!form.imageData ? (
                      <>
                        <UploadIcon>📸</UploadIcon>
                        <UploadText>Tap here to upload your sail design</UploadText>
                        <UploadHint>JPG, PNG or GIF · Maximum 5MB</UploadHint>
                      </>
                    ) : (
                      <>
                        <UploadText style={{ color: '#9d6d49', fontWeight: 700 }}>
                          ✅ {form.imageFileName}
                        </UploadText>
                        <UploadHint>Tap to choose a different image</UploadHint>
                      </>
                    )}
                  </UploadZone>
                  {form.imageData && (
                    <ImagePreview>
                      <img src={form.imageData} alt="Sail design preview" />
                    </ImagePreview>
                  )}
                  {errors.image && <ErrorText>{errors.image}</ErrorText>}
                </FieldGroup>

                <ConsentBox>
                  <CheckboxWrapper>
                    <input
                      type="checkbox"
                      name="consentPublic"
                      checked={form.consentPublic}
                      onChange={handleChange}
                    />
                    <ConsentText>
                      <strong>I agree my sail design can be shown publicly.</strong> If approved,
                      my first name, age, and sail design may be displayed on the Belvoir Castle
                      website and within the castle grounds. The winning design may be used to
                      make a physical sail for the ship.
                    </ConsentText>
                  </CheckboxWrapper>
                  {errors.consentPublic && (
                    <ErrorText style={{ marginLeft: '2rem', marginTop: '0.35rem' }}>{errors.consentPublic}</ErrorText>
                  )}

                  <CheckboxWrapper style={{ marginTop: '0.875rem' }}>
                    <input
                      type="checkbox"
                      name="consentTerms"
                      checked={form.consentTerms}
                      onChange={handleChange}
                    />
                    <ConsentText>
                      <strong>A parent or guardian approves this submission.</strong> This design
                      is my own original work and I give Belvoir Castle permission to display and
                      potentially reproduce it as a physical sail for the ship.
                    </ConsentText>
                  </CheckboxWrapper>
                  {errors.consentTerms && (
                    <ErrorText style={{ marginLeft: '2rem', marginTop: '0.35rem' }}>{errors.consentTerms}</ErrorText>
                  )}
                </ConsentBox>

                {submitError && (
                  <ErrorText style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                    ⚠️ {submitError}
                  </ErrorText>
                )}

                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? '⏳ Sending your design…' : '🏴‍☠️ Enter the Competition!'}
                </SubmitButton>
              </form>
            </FormCard>
          </>
        ) : (
          <SuccessCard>
            <SuccessEmoji>🏴‍☠️</SuccessEmoji>
            <SuccessTitle>Ahoy, {submittedEntry?.childName}!</SuccessTitle>
            <SuccessText>
              Your sail design "<strong>{submittedEntry?.title}</strong>" has been received and entered into the competition.
            </SuccessText>
            <SuccessText>
              <strong>Right now it's in review.</strong> Our crew checks each entry before it goes live.
              <strong> Once approved</strong>, it will show up on the pirate sail gallery for everyone to see — check back soon.
            </SuccessText>
            <SuccessHighlight>
              🏆 If your design wins, it will be sewn into a real sail and hoisted on the
              pirate ship in the Belvoir Castle adventure playground — and you&apos;ll also win a family ticket to Belvoir Castle BOOM Circus!
            </SuccessHighlight>
            <SuccessText>
              Good luck, brave pirate — thanks for taking part!
            </SuccessText>
            <SuccessActions>
              <ActionButton to="/childrens-art-gallery" className="primary">
                🖼️ View the Gallery
              </ActionButton>
              <AnotherButton onClick={handleSubmitAnother}>
                🏴‍☠️ Submit Another Design
              </AnotherButton>
            </SuccessActions>
          </SuccessCard>
        )}
      </ContentArea>

      <Footer />
    </PageContainer>
  );
};

export default ChildrensArtSubmit;
